import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import useTheme from '../hooks/useTheme.ts';
import { toast } from 'react-toastify';
import styles from './CopyToStagesModal.module.css';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

// Matches the wells table / GET /pads/:padId/wells
interface Well {
  id: number;
  well_name: string;
  stage_count: number;
}

interface WellCopyData {
  wellId: number;
  allStages: boolean;
  stageStart: number | '';
  stageEnd: number | '';
  overflush: number | '';
}

interface CopyToStagesModalProps {
  show: boolean;
  onHide: () => void;
  padId: number | null;
  sourceStageId: number | null;
  sourceStageNumber?: number | null;
  useFakeData?: boolean;
}

// Only used when useFakeData is on — the real list comes from the API.
const defaultFakeWells: Well[] = [
  { id: 1, well_name: 'Blackstone 1H', stage_count: 32 },
  { id: 2, well_name: 'Blackstone 2H', stage_count: 31 },
  { id: 3, well_name: 'Blackstone 3H', stage_count: 33 },
  { id: 4, well_name: 'Blackstone 4H', stage_count: 30 },
  { id: 5, well_name: 'Blackstone 5H', stage_count: 34 },
];

// "stage 3" / "stages 3 to 7" / "stages 1, 2, 4, 5" — the source stage is skipped
// when it falls inside the range, so the list isn't always contiguous and we
// can't just print start-to-end.
const describeStages = (nums: number[]): string => {
  if (nums.length === 0) return 'no stages';
  if (nums.length === 1) return `stage ${nums[0]}`;
  const contiguous = nums.every((n, i) => i === 0 || n === nums[i - 1] + 1);
  return contiguous
    ? `stages ${nums[0]} to ${nums[nums.length - 1]}`
    : `stages ${nums.join(', ')}`;
};

const CopyToStagesModal: React.FC<CopyToStagesModalProps> = ({
  show,
  onHide,
  padId,
  sourceStageId,
  sourceStageNumber = null,
  useFakeData = false
}) => {
  const { theme } = useTheme();

  const [wellsList, setWellsList] = useState<Well[]>([]);
  const [wellsLoading, setWellsLoading] = useState<boolean>(false);
  const [wellsError, setWellsError] = useState<string | null>(null);
  const [wellCopyData, setWellCopyData] = useState<{ [wellId: number]: WellCopyData }>({});
  const [copyingWellId, setCopyingWellId] = useState<number | null>(null);

  // Load the pad's wells whenever the modal opens.
  useEffect(() => {
    if (!show) return;

    if (useFakeData) {
      setWellsList(defaultFakeWells);
      return;
    }

    if (!padId) {
      setWellsList([]);
      setWellsError('No pad associated with this stage.');
      return;
    }

    const fetchWells = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setWellsError('Authentication token not found');
        return;
      }
      setWellsLoading(true);
      setWellsError(null);
      try {
        const res = await axios.get(`${backendUrl}/pads/${padId}/wells`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const rows: Well[] = (Array.isArray(res.data) ? res.data : []).map((w: any) => ({
          id: w.id,
          well_name: w.well_name,
          stage_count: Number(w.stage_count) || 0,
        }));
        setWellsList(rows);
        if (rows.length === 0) setWellsError('No wells found on this pad.');
      } catch (err) {
        console.error('Error fetching wells:', err);
        setWellsList([]);
        // Don't render an empty list as "no wells" — that's a different problem.
        setWellsError('Failed to load wells for this pad.');
      } finally {
        setWellsLoading(false);
      }
    };

    fetchWells();
  }, [show, padId, useFakeData]);

  // Reset the per-well form whenever the well list changes.
  useEffect(() => {
    const initialData: { [wellId: number]: WellCopyData } = {};
    wellsList.forEach(well => {
      initialData[well.id] = {
        wellId: well.id,
        allStages: false,
        stageStart: '',
        stageEnd: '',
        overflush: ''
      };
    });
    setWellCopyData(initialData);
  }, [wellsList]);

  const handleAllStagesToggle = (wellId: number, well: Well) => {
    setWellCopyData(prev => {
      const currentData = prev[wellId];
      const newAllStages = !currentData.allStages;

      return {
        ...prev,
        [wellId]: {
          ...currentData,
          allStages: newAllStages,
          stageStart: newAllStages ? 1 : '',
          stageEnd: newAllStages ? well.stage_count : ''
        }
      };
    });
  };

  const handleInputChange = (wellId: number, field: 'stageStart' | 'stageEnd' | 'overflush', value: string) => {
    const numValue = value === '' ? '' : parseFloat(value);

    setWellCopyData(prev => ({
      ...prev,
      [wellId]: {
        ...prev[wellId],
        [field]: numValue,
        // If manually editing stage fields, uncheck All Stages
        ...(field !== 'overflush' ? { allStages: false } : {})
      }
    }));
  };

  const handleCopyToStages = async (well: Well) => {
    const data = wellCopyData[well.id];

    if (!data || data.stageStart === '' || data.stageEnd === '') {
      toast.warning(`Please enter Stage Start and Stage End for ${well.well_name}`, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const start = Number(data.stageStart);
    const end   = Number(data.stageEnd);

    if (start < 1 || end > well.stage_count) {
      toast.warning(`${well.well_name} has stages 1–${well.stage_count}`, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    if (start > end) {
      toast.warning(`Stage Start must not be after Stage End for ${well.well_name}`, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!sourceStageId) {
      toast.error('No source stage to copy from');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    setCopyingWellId(well.id);
    try {
      const res = await axios.post(
        `${backendUrl}/stages/${sourceStageId}/proposal_treatment_schedule/copy`,
        {
          targets: [{
            well_id: well.id,
            stage_start: start,
            stage_end: end,
            overflush: data.overflush === '' ? null : Number(data.overflush),
          }]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // An empty stage_numbers means the range held nothing but the source stage,
      // which the backend skips. That's a no-op, not a failure — the range already
      // has the schedule being copied, so report it the same as any other success.
      const nums: number[] = res.data?.results?.[0]?.stage_numbers ?? [];
      const described = nums.length > 0
        ? describeStages(nums)
        : describeStages(Array.from({ length: end - start + 1 }, (_, i) => start + i));

      toast.success(`${well.well_name} — ${described} updated`, {
        position: "top-right",
        autoClose: 4000,
      });
    } catch (err: any) {
      console.error('Error copying proposal to stages:', err);
      toast.error(err?.response?.data?.message || `Failed to copy to ${well.well_name}`, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setCopyingWellId(null);
    }
  };

  const handleCancel = (wellId: number) => {
    setWellCopyData(prev => ({
      ...prev,
      [wellId]: {
        wellId: wellId,
        allStages: false,
        stageStart: '',
        stageEnd: '',
        overflush: ''
      }
    }));
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      dialogClassName={styles.copyToStagesModal}
    >
      <Modal.Header
        closeButton
        style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          borderBottom: `1px solid #5C646B`
        }}
      >
        <Modal.Title style={{ color: theme === 'dark' ? '#8CC540' : '#303335' }}>
          Copy Proposal to Stages
          {sourceStageNumber != null && (
            <span style={{
              fontSize: '0.8em',
              fontWeight: 400,
              marginLeft: '10px',
              color: theme === 'dark' ? '#9ca3af' : '#6b7280'
            }}>
              from Stage {sourceStageNumber}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          color: theme === 'dark' ? '#ffffff' : '#303335',
          maxHeight: '70vh',
          overflowY: 'auto',
          padding: '16px'
        }}
      >
        {wellsLoading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant={theme === 'dark' ? 'light' : 'primary'} />
            <p className="mt-3">Loading wells...</p>
          </div>
        ) : wellsError ? (
          <div className="alert alert-warning mb-0">{wellsError}</div>
        ) : (
          <div className={styles.wellsContainer}>
            {wellsList.map((well, index) => {
              const data = wellCopyData[well.id] || {
                allStages: false,
                stageStart: '',
                stageEnd: '',
                overflush: ''
              };

              return (
                <div
                  key={well.id}
                  className={styles.wellSection}
                  style={{
                    backgroundColor: theme === 'dark' ? '#1a1d1f' : '#f8f9fa',
                    borderColor: theme === 'dark' ? '#5C646B' : '#dee2e6',
                    marginBottom: index < wellsList.length - 1 ? '12px' : '0'
                  }}
                >
                  {/* Well Header */}
                  <div className={styles.wellHeader}>
                    <h5 style={{
                      margin: 0,
                      color: theme === 'dark' ? '#8CC540' : '#303335',
                      fontWeight: 600,
                      fontSize: '15px'
                    }}>
                      {well.well_name}
                    </h5>
                    <span style={{
                      fontSize: '12px',
                      color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                    }}>
                      ({well.stage_count} stages)
                    </span>
                  </div>

                  {/* Form Row */}
                  <div className={styles.formRow}>
                    {/* All Stages Radio */}
                    <div className={styles.formGroup}>
                      <Form.Check
                        type="radio"
                        id={`allStages-${well.id}`}
                        label="All Stages"
                        checked={data.allStages}
                        onChange={() => handleAllStagesToggle(well.id, well)}
                        style={{
                          color: theme === 'dark' ? '#ffffff' : '#303335',
                          marginBottom: 0
                        }}
                        className={theme === 'dark' ? 'text-light' : ''}
                      />
                    </div>

                    {/* Stage Start */}
                    <div className={styles.formGroup}>
                      <Form.Label
                        style={{
                          fontSize: '12px',
                          marginBottom: '4px',
                          color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                        }}
                      >
                        Stage Start
                      </Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                        min={1}
                        max={well.stage_count}
                        value={data.stageStart}
                        onChange={(e) => handleInputChange(well.id, 'stageStart', e.target.value)}
                        placeholder="1"
                        style={{
                          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                          color: theme === 'dark' ? '#ffffff' : '#303335',
                          borderColor: theme === 'dark' ? '#5C646B' : '#ced4da',
                          width: '80px'
                        }}
                      />
                    </div>

                    {/* Stage End */}
                    <div className={styles.formGroup}>
                      <Form.Label
                        style={{
                          fontSize: '12px',
                          marginBottom: '4px',
                          color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                        }}
                      >
                        Stage End
                      </Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                        min={1}
                        max={well.stage_count}
                        value={data.stageEnd}
                        onChange={(e) => handleInputChange(well.id, 'stageEnd', e.target.value)}
                        placeholder={well.stage_count.toString()}
                        style={{
                          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                          color: theme === 'dark' ? '#ffffff' : '#303335',
                          borderColor: theme === 'dark' ? '#5C646B' : '#ced4da',
                          width: '80px'
                        }}
                      />
                    </div>

                    {/* Overflush */}
                    <div className={styles.formGroup}>
                      <Form.Label
                        style={{
                          fontSize: '12px',
                          marginBottom: '4px',
                          color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                        }}
                      >
                        Overflush (Bbls)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        className={theme === 'dark' ? styles.inputDark : styles.inputLight}
                        min={0}
                        step={0.1}
                        value={data.overflush}
                        onChange={(e) => handleInputChange(well.id, 'overflush', e.target.value)}
                        placeholder="100"
                        style={{
                          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                          color: theme === 'dark' ? '#ffffff' : '#303335',
                          borderColor: theme === 'dark' ? '#5C646B' : '#ced4da',
                          width: '100px'
                        }}
                      />
                    </div>

                    {/* Buttons */}
                    <div className={styles.buttonGroup}>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleCancel(well.id)}
                        disabled={copyingWellId === well.id}
                        style={{
                          borderColor: theme === 'dark' ? '#5C646B' : '#ced4da',
                          color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCopyToStages(well)}
                        disabled={copyingWellId !== null}
                        style={{
                          backgroundColor: '#8CC540',
                          borderColor: '#8CC540',
                          color: '#000000'
                        }}
                      >
                        {copyingWellId === well.id ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Copying
                          </>
                        ) : 'Copy to Stages'}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer
        style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          borderTop: `1px solid #5C646B`
        }}
      >
        <Button
          variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'}
          onClick={onHide}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CopyToStagesModal;